import React from 'react';
import { HelpPanel, Icon, Link } from '@amzn/awsui-components-react-v3';
import '../../styles/spotcp.css';

export const InfoLink = ({ id, onFollow }) => (
  <Link variant="info" id={id} onFollow={onFollow}>
    Info
  </Link>
);

const externalLinkProps = {
  external: true,
  externalIconAriaLabel: 'Opens in a new tab',
};

// a special case of external link, to be used within a link group, where all of them are external
// and we do not repeat the icon
export const ExternalLinkItem = ({ href, text }) => (
  <Link href={href} ariaLabel={`${text} ${externalLinkProps.externalIconAriaLabel}`} target="_blank">
    {text}
  </Link>
);

export function AccuracyInfo() {
  return (
    <HelpPanel header={<h2>Accuracy Calculations</h2>}>
      <h3>How do we define accuracy?</h3>
      <div>
        The accuracy metric is a monthly comparison of the aggregated Daily CP (first published value within 24 hours of shipment,
        <b> first answer</b>) component values to an established benchmark. All components are compared against the benchmark resulting in
        a $ error for each intersection of marketplace, channel, and product line.  The absolute monetary error is aggregated and divided
        by the total absolute benchmark amount to produce an error percentage for each month.  The metric serves as an error metric and
        doesn’t net out variances, holding accountability for the accuracy of each component down to the product line level.
        We will also be including the monetary per unit variance by component per country/channel to provide the impact of each component
        to the overall Net CP variance.
      </div>

      <h3>How do I bridge P&L Benchmark to Fortune?</h3>
      <div>
        To bridge P&L Benchmark and Accuracy Benchmark to Fortune where CP is using Cognos accounts it is important to consider the
        exclusions that CP makes with regard to Warehouse Deals Channels, Amazon Global Listings and Exclusion of Cognos accounts.
        There are 2 columns provided in the tool to help with bridging - P&L exclusions and P&L channel Exclusions. The total of P&L
        Benchmark and the 2 exclusions columns should bridge to the values seen in Fortune. If you still see a disconnect this could
        be due to a difference in the PLs considered in the Physical consumer roll up which can be viewed through the PL detail report
        and PL/GL mapping interface.
      </div>

      <h3>How do you decide what to consider an exclusion?</h3>
      <div>
         When comparing CP versus P&L, it should only be done on an apples to apples basis. There are some accounts in the P&L that will
         never be reflected in CP as they are not shipment based and rather are reserves and/or reflect balance sheet adjustments and
         therefore should not be considered when comparing against each other. SPS Finance is responsible for auditing relevance of P&L
         accounts and acting as controller in this space. All such accounts are marked with an explicit <b>Excluded</b> badging.
      </div>

      <h3> Are there any other special filtering on CP data from O_WBR_CP for accuracy purposes? </h3>
      <div>
        Please refer to this wiki for more information on special filters -
        <ExternalLinkItem href="https://w.amazon.com/bin/view/ProfitIntelligence/PITools/Special_Filtering" text="Special Filtering" />
      </div>
    </HelpPanel>
  );
}

export function LiquidationCOGSInfo() {
  return (
    <HelpPanel header={<h2>Liquidation COGS Accuracy Benchmark</h2>}>
      <h3> How is the Accuracy Benchmark calculated? </h3>
      <div>
        The Accuracy Benchmark of Liquidation COGS is rolled up from it's subcomponents.
        The benchmarks for each subcomponent are calculated separately and then added together to form the Liquidation COGS benchmark.
      </div>
      <br/>
      <div>
        <h3> Steps for Calculation </h3>
        <ol>
          <li>Aggregate the trailing 12 months (T12M) data including the current month actuals for the respective Cognos account
              and multiply it with the split ratio which will be calculated using
              <ExternalLinkItem
                href="https://datacentral.a2z.com/dw-platform/servlet/dwp/template/EtlViewExtractJobs.vm/job_profile_id/9969153"
                text=" Split Ratio Query" />.
          </li>
          <li>Aggregate the T12M data for HCP driver(PCOGS less of <ExternalLinkItem
            href="https://datacentral.a2z.com/dw-platform/servlet/dwp/template/EtlViewExtractJobs.vm/job_profile_id/9969178"
            text=" Vendor Returned Exclusions" />) for each GL, CH, MP and subcomponent.</li>
          <li>Take a ratio of the metrics calculated in Step (1) and (2) for each GL.</li>
          <li>Multiply the GL level rate calculated in (3) with the respective HCP driver for the current month to recreate
              the benchmark for each MP-GL-CH-sub-component combination.</li>
          <li>Total benchmark for the GL for the entire component will be a sum of the benchmark across the 3 sub-components.</li>
          <li>Abs Variance is calculated at the component level and not at the sub-component level.</li>
        </ol>
      </div>
      <h3> General formula for Liquidation benchmarks </h3>
      <pre>
          Benchmark = (T12M Cognos Account * Split Ratio / T12M HCP Driver) * Current month HCP Driver
      </pre>
      <h3>Overstock/Damages Accuracy Benchmark</h3>
      <pre>
          Overstock Benchmark = ((T12M 54910 * Overstock ratio) / (T12M  CP Last Answer PCOGS - T12M Vendor Returned Exclusions))
          * Current month ( CP Last Answer PCOGS - Vendor Returned Exclusions)
      </pre>
      <pre>
          Damages Benchmark = ((T12M 54910 - T12M Overstock Fifo cost) / (T12M  CP Last Answer PCOGS - T12M Vendor Returned Exclusions))
          * Current month ( CP Last Answer PCOGS - Vendor Returned Exclusions)
      </pre>
      The <b>overstock ratio</b> is calculated using the Liquidations <ExternalLinkItem
      href="https://datacentral.a2z.com/dw-platform/servlet/dwp/template/EtlViewExtractJobs.vm/job_profile_id/9969153"
      text="Split Ratio Query" />. The damages sum is calculated as (T12M 54910 - Overstock sum). It calculates the <b>sum(fifo_cost)</b>
       for each marketplace_id, shp_month, GL, channel and subcomponent combination.
      <h3>Disposed Accuracy Benchmark</h3>
      <pre>
          Benchmark =  ((T12M 53501 + T12M 53505) / (T12M  CP Last Answer PCOGS - T12M Vendor Returned Exclusions))
          * Current month ( CP Last Answer PCOGS - Vendor Returned  Exclusions)
      </pre>
    </HelpPanel>
  );
}

export function LiquidationRevenueInfo() {
  return (
    <HelpPanel header={<h2>Liquidation Revenue Accuracy Benchmark</h2>}>
      <h3> How is the Accuracy Benchmark calculated? </h3>
      <div>
        The Accuracy Benchmark of Liquidation Revenue is rolled up from it's subcomponents.
        The benchmarks for each subcomponent are calculated separately and then added together to form the Liquidation Revenue benchmark.
      </div>
      <br/>
      <div>
        <h3> Steps for Calculation </h3>
        <ol>
          <li>Aggregate the trailing 12 months (T12M) data including the current month actuals for the respective Cognos account
              and multiply it with the split ratio which will be calculated using the
              <ExternalLinkItem
                href="https://datacentral.a2z.com/dw-platform/servlet/dwp/template/EtlViewExtractJobs.vm/job_profile_id/9969153"
                text=" Split Ratio Query" />.
          </li>
          <li>Aggregate the T12M data for HCP driver(Liquidation COGS benchmark) for each GL, CH, MP and subcomponent.</li>
          <li>Take a ratio of the metrics calculated in Step (1) and (2) for each GL.</li>
          <li>Multiply the GL level rate calculated in (3) with the respective HCP driver for the current month to recreate
              the benchmark for each MP-GL-CH-sub-component combination.</li>
          <li>Total benchmark for the GL for the entire component will be a sum of the benchmark across the 3 sub-components.</li>
          <li>Abs Variance is calculated at the component level and not at the sub-component level.</li>
        </ol>
      </div>
      <h3> General formula for Liquidation benchmarks </h3>
      <pre>
          Benchmark = (T12M Cognos Account * Split Ratio / T12M HCP Driver) * Current month HCP Driver
      </pre>
      <h3>Overstock/Damages Accuracy Benchmark</h3>
      <pre>
          Overstock Benchmark = ((T12M 40135 * Overstock ratio) / (T12M  Liquidation COGS benchmark for overstock))
          * Current month (Liquidation COGS benchmark for overstock)
      </pre>
      <pre>
          Damages Benchmark = ((T12M 40135 - T12M Overstock Refund Amount) / (T12M  Liquidation COGS benchmark for damages))
          * Current month (Liquidation COGS benchmark for damages)
      </pre>
      The <b>overstock ratio</b> is calculated using the Liquidations <ExternalLinkItem
      href="https://datacentral.a2z.com/dw-platform/servlet/dwp/template/EtlViewExtractJobs.vm/job_profile_id/9969153"
      text="Split Ratio Query" />. The damages sum is calculated as (T12M 40135 - Overstock sum). It calculates the <b>sum(refund_amount)
      </b> for each marketplace_id, shp_month, GL, channel and subcomponent combination.
    </HelpPanel>
  );
}

export function VariableFCCostsInfo() {
  return (
    <HelpPanel header={<h2>Variable FC Costs Accuracy Benchmark</h2>}>
      <h3> How is the Accuracy Benchmark calculated? </h3>
      <div>
        The CP data source for Variable FC Costs is updated from Applied CP table to Accrued CP table as of 09/01/2021 for US marketplace.
      </div>
      <div>
        <b> FIRST ANSWER SOURCE: </b> <pre> CONTRIBUTION_DDL.TVFC_ACCRUED_FA </pre>
        <b> LAST ANSWER SOURCE: </b> <pre> CONTRIBUTION_DDL.TVFC_ACCRUED_LA </pre>
        For the rest of the marketplaces and report months prior to 09/01/2021, Variable FC Costs CP amounts are still fetched from O_WBR_CP tables.
        More details on data load query
        <ExternalLinkItem
          href="https://datacentral.a2z.com/dw-platform/servlet/dwp/template/EtlViewExtractJobs.vm/job_profile_id/10083210"
          text=" here" />.
      </div>
      <br/>
      <div>
        <h3> Why did we change the CP data source for Accuracy Benchmark calculations? </h3>
        CP and Cognos have difference in timing related to cost recognition of Variable FC related costs. CP recognizes costs related to all
        shipments in a month, whereas cognos recognizes all accrued costs in a given month which also includes inbound related events.
        This creates an inherent difference in our FA/LA vs the benchmark and is the main source of inaccuracy.
        <br/>
        Today, the data we pull from CP is using the Applied (<b>O_WBR_CP</b>) table, which publishes inbound, transfer, and outbound costs accrued
        in past 6 months when a shipment event is created. Whereas in Cognos, the balances reflect the cost accrued till month end.
        To remove this timing difference in our accuracy calculations, we are moving away from using the Applied table for Variable FC Costs,
        and instead, we will use the Accrued CP (<b> CONTRIBUTION_DDL.TVFC_ACCRUED_FA/CONTRIBUTION_DDL.TVFC_ACCRUED_LA </b>) tables which store
        accrued balances for all inventory events. The Accrued CP tables is transactional in nature and is a more accurate data source for
        accuracy calculations for Variable FC Costs.
      </div>
    </HelpPanel>
  );
}

export function ContributionProfitInfo() {
  return (
    <HelpPanel header={<h2>Contribution Profit Accuracy Benchmark</h2>}>
      <h3> How is the Accuracy Benchmark calculated? </h3>
      <div>
        The CP data source for Variable FC Costs is updated from Applied CP table to Accrued CP table as of 09/01/2021 for US marketplace.
        Correspondingly, First Answer and Last Answer calculations for Contribution Profit is updated to
        <pre> CP - Applied CP Variable FC Cost + Accrued CP Variable FC Costs </pre> as of 09/01/2021 for US marketplace.
      </div>
      <br/>
      <div>
        <h3> Why did we change the CP data source for Accuracy Benchmark calculations? </h3>
        CP and Cognos have difference in timing related to cost recognition of Variable FC related costs. CP recognizes costs related to all
        shipments in a month, whereas cognos recognizes all accrued costs in a given month which also includes inbound related events.
        This creates an inherent difference in our FA/LA vs the benchmark and is the main source of inaccuracy.
        <br/>
        Today, the data we pull from CP is using the Applied (<b>O_WBR_CP</b>) table, which publishes inbound, transfer, and outbound costs accrued
        in past 6 months when a shipment event is created. Whereas in Cognos, the balances reflect the cost accrued till month end.
        To remove this timing difference in our accuracy calculations, we are moving away from using the Applied table for Variable FC Costs,
        and instead, we will use the Accrued CP (<b> CONTRIBUTION_DDL.TVFC_ACCRUED_FA/CONTRIBUTION_DDL.TVFC_ACCRUED_LA </b>) tables which store
        accrued balances for all inventory events. The Accrued CP tables is transactional in nature and is a more accurate data source for
        accuracy calculations for Variable FC Costs.
      </div>
    </HelpPanel>
  );
}



// WEBPACK FOOTER //
// ./src/components/mapper/accuracy_benchmark_info.jsx